import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'

import { useRegister } from '../../../hooks/auth/useRegister'

import Icon from '../../ui/Icon'
import { Button } from '../../buttons/Button'
import { TextInput } from '../../input/TextInput'
import { PasswordCreateInput } from '../../input/passwordCreateInput/PasswordCreateInput'
import { CheckboxInput } from '../../input/checkboxInput/CheckboxInput'

import { useApp } from '../../../contexts/AppContext'
import { FormField } from '../../../types/formData'
import { RegisterDetails } from '../../../types/user/auth'
import { BannerKind } from '../../../types/contexts/AppContext'
import {
  CompletionMode,
  type CompletionRedirectState,
} from '../../../types/account/accountSetup'
import { InputType } from '../../../constants/inputTypes'
import { FeatureFlags, isFeatureEnabled } from '../../../constants/featureFlags'
import { CONFIG } from '../../../config'

interface RegisterFormData {
  email: FormField<string>
  password: FormField<string>
  termsConsent: FormField<boolean>
}

export const RegisterForm = () => {
  const { t } = useTranslation(['register', 'login'])
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const appRegister = useRegister()
  const [formData, setFormData] = useState<RegisterFormData>({
    email: { value: '', isValid: false },
    password: { value: '', isValid: false },
    termsConsent: { value: false, isValid: false },
  })

  useEffect(() => {
    if (appRegister.error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: appRegister.error,
      })
    }
  }, [appRegister.error, showBanner])

  const handleRegister = async () => {
    const response = await appRegister.register({
      email: formData.email.value,
      password: formData.password.value,
      termsConsent: formData.termsConsent.value,
    } as RegisterDetails)
    if (response) {
      const state: CompletionRedirectState = {
        mode: CompletionMode.ONBOARDING,
        returnTo: '/dashboard',
      }

      navigate('/account/setup', { state })
    }
  }

  const handleInputChange = (
    name: string,
    value: string | boolean | File | null,
    isValid: boolean
  ) => {
    setFormData(prev => ({ ...prev, [name]: { value, isValid } }))
  }

  const validForm = () => Object.values(formData).every(input => input.isValid)

  const handleGoogleRegister = () => {
    window.location.href = `${CONFIG.BOUNCE_API}/oauth2/authorization/google`
  }

  return (
    <form className="fly-form">
      <Button
        className="fly-btn-back"
        variant="ghost"
        size="sm"
        icon={<Icon name="chevL" size={14} />}
        label={t('register:back')}
        onClick={() => navigate('/')}
      />

      <div className="eyebrow">{t('register:eyebrow')}</div>
      <h1 className="display fly-text-title">{t('register:title')}</h1>
      <p className="fly-text-subtitle">{t('register:subtitle')}</p>

      {isFeatureEnabled(FeatureFlags.FF_REGISTER) && (
        <>
          <Button
            className="btn-google"
            variant="ghost"
            block
            icon={<Icon name="google" size={18} />}
            label={t('login:google-btn')}
            onClick={handleGoogleRegister}
          />
          <div className="divider-or">{t('login:divider')}</div>
        </>
      )}

      <div className="ph-col">
        <TextInput
          name="email"
          label={t('auth:email')}
          required={true}
          type={InputType.Email}
          placeHolder={t('auth:email')}
          value={formData.email.value}
          onChange={handleInputChange}
          infoMessage={t('register:email-info')}
          errorMessage={t('register:email-error')}
        />
        <PasswordCreateInput
          label={t('auth:password')}
          placeHolder={t('auth:password')}
          confirmLabel={t('auth:confirm-password')}
          confirmPlaceHolder={t('auth:confirm-password')}
          name="password"
          value={formData.password.value}
          onChange={handleInputChange}
          errorMessage={t('register:password-error')}
        />

        <div className="register-terms">
          <CheckboxInput
            name="termsConsent"
            required
            checked={formData.termsConsent.value}
            onChange={handleInputChange}
            label={
              <Trans
                i18nKey="register:terms"
                components={{
                  terms: <Link to="/terms" />,
                  privacy: <Link to="/terms#c10" />,
                }}
              />
            }
          />
        </div>

        <Button
          className="btn-submit"
          size="lg"
          block
          loading={appRegister.loading}
          disabled={!validForm()}
          label={
            appRegister.loading ? t('register:loading') : t('register:submit')
          }
          icon={<Icon name="arrowR" size={16} />}
          iconPosition="right"
          onClick={handleRegister}
        />

        <p className="gfly-footer">
          {t('register:signin-prompt')}{' '}
          <Link to="/login">{t('register:signin-link')}</Link>
        </p>
      </div>
    </form>
  )
}
